'use client';

import { cancelSubscription, Subscription } from '@3fourteen/core';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import { mainBugsnag } from 'helpers/bugsnag';
import { memo, useCallback, useState } from 'react';

import { Button } from 'components';

interface CancelSubscriptionModalProps {
  id: Subscription['id'];
}

function CancelSubscriptionModal({ id }: CancelSubscriptionModalProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const onCancel = async () => {
    setLoading(true);

    try {
      await cancelSubscription(id);

      setOpen(false);
    } catch (error: any) {
      setErrorMessage(
        'Looks like there might have been an issue canceling your subscription. Please reach out to us directly at info@3fourteenresearch.com.'
      );

      mainBugsnag.notify(error, (event) => {
        event.context = 'onCancel() in CancelSubscriptionModal';
        event.addMetadata('Sx Info', {
          id,
        });
      });
    } finally {
      setLoading(false);

      await queryClient.refetchQueries({
        queryKey: ['member'],
        type: 'active',
        exact: true,
      });
    }
  };

  const onOpen = useCallback(() => {
    setErrorMessage('');
    setOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <div>
      <Button onClick={onOpen} className='btn-xs px-1 flex justify-center'>
        <span className='font-bold text-red-700 ml-4'>Cancel</span>
      </Button>
      <Transition show={open}>
        <Dialog as='div' className='relative z-10' onClose={setOpen}>
          <TransitionChild
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'>
            <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </TransitionChild>

          <div className='fixed inset-0 z-10 overflow-y-auto'>
            <div className='flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0'>
              <TransitionChild
                enter='ease-out duration-300'
                enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                enterTo='opacity-100 translate-y-0 sm:scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 translate-y-0 sm:scale-100'
                leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'>
                <DialogPanel className='relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6'>
                  <div>
                    <div className='mt-3 text-center sm:mt-5'>
                      <DialogTitle
                        as='h3'
                        className='text-lg font-medium leading-6 text-gray-900 mb-4'>
                        Cancel your subscription
                      </DialogTitle>
                      <div className='mt-2 text-gray-700 mb-8'>
                        {!errorMessage ? (
                          <p>
                            Are you sure you want to cancel your subscription to the Portfolio
                            Builder?
                          </p>
                        ) : (
                          <p>{errorMessage}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='mt-5 sm:mt-6 sm:flex justify-center'>
                    {!errorMessage && (
                      <Button
                        type='button'
                        isLoading={loading}
                        className='btn  bg-red-700 text-white w-full mx-2'
                        onClick={onCancel}>
                        Yes, I want to cancel
                      </Button>
                    )}
                    <Button
                      type='button'
                      className='btn border border-solid border-gray-500 text-gray-800 w-full mx-2'
                      onClick={onClose}>
                      {!errorMessage ? "No, I'd like to keep it" : 'OK'}
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

CancelSubscriptionModal.displayName = 'CancelSubscriptionModal';

export default memo(CancelSubscriptionModal);
